import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const Profile = (mode) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [error, setError] = useState(null);
  const [isUsernameDialog, setIsUsernameDialog] = useState(false);
  const [isPasswordDialog, setIsPasswordDialog] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [action, setAction] = useState(null);
  const [streetAddress, setStreetAddress] = useState("");
  const [city, setCity] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [purchasedTools, setPurchasedTools] = useState([]);
  const [loading, setLoading] = useState(true);

  // dummy subscription data
  const subscription = {
    plan: "Pro",
    status: "Active",
    expiresOn: "2025-01-01",
  };

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      const token = localStorage.getItem("jwt");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/users/me`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setUser(response.data);
      } catch (err) {
        setError("Failed to fetch user data. Please log in again.");
      }
    };

    fetchUserData();
  }, [navigate]);

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("jwt");
    navigate("/");
    window.location.reload();
  };

  // Function for username change
  const handleUsernameUpdate = async () => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      setMessage("You are not authenticated.");
      return;
    }

    try {
      // Fetch user details to get the user ID
      const userResponse = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!userResponse.ok) {
        throw new Error("Failed to fetch user details.");
      }

      const userData = await userResponse.json();
      const userId = userData.id;

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ username: newUsername }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update username.");
      }

      setMessage("Username updated successfully.");
      setIsUsernameDialog(false);
      setUser({ ...user, username: newUsername });

      toast.success("Username successfully changed!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      setMessage("Error updating username: " + error.message);
    }
  };

  // Function to update address
  const handleAddressUpdate = async () => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      navigate("/");
      return;
    }

    const updatedAddress = {
      streetAddress,
      city,
      postalCode,
    };

    try {
      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userId = userResponse.data.id;

      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}`,
        {
          address: updatedAddress,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setUser({ ...userResponse.data, address: updatedAddress });
      setIsUsernameDialog(false);

      toast.success("Address updated successfully!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Failed to update address:", err);
      alert("Failed to update address. Please try again.");
    }
  };

  // Function for password reset
  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      return;
    }

    const token = localStorage.getItem("jwt");
    if (!token) {
      setMessage("You are not authenticated.");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/auth/change-password`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            currentPassword,
            password: newPassword,
            passwordConfirmation: confirmPassword,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to reset password.");
      }

      setNewPassword("");
      setConfirmPassword("");
      setCurrentPassword("");
      setIsPasswordDialog(false);

      toast.success("Password changed successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      setMessage("Error resetting password: " + error.message);
    }
  };

  // Confirm action dialog (username change, password change, or logout)
  const handleConfirmation = () => {
    if (action === "username") {
      handleUsernameUpdate();
    } else if (action === "address") {
      handleAddressUpdate();
    } else if (action === "password") {
      handleResetPassword();
    } else if (action === "logout") {
      handleLogout();
    }
    setShowConfirmation(false);
  };

  const showConfirmationDialog = (actionType) => {
    setAction(actionType);
    setShowConfirmation(true);
  };

  useEffect(() => {
    fetchPurchasedTools();
  }, []);

  // fetch purchased tools
  const fetchPurchasedTools = async () => {
    const token = localStorage.getItem("jwt");
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      // Fetch user data to get the logged-in user's email
      const userRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/users/me`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const userEmail = userRes.data.email;

      // Fetch orders data
      const ordersRes = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/orders?filters[userEmail][$eq]=${userEmail}&filters[status][$eq]=successful&populate=tool`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (ordersRes.data && Array.isArray(ordersRes.data)) {
        const validOrders = ordersRes.data
          .filter((order) => order.tool && order.tool.toolName)
          .map((order) => ({
            amount: order.amount,
            tool: {
              toolName: order.tool.toolName,
              toolImage: order.tool.toolImage,
            },
          }));
        setPurchasedTools(validOrders);
      }
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Failed to load purchased tools");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center text-white">
        Loading...
      </div>
    );
  }

  return (
    <div className="profile-container h-screen flex flex-col items-center p-6">
      {/* side buttons */}
      <>
        {/* wishlist button */}
        <Link to="/wishlist">
          <button className="absolute right-5 login inline-flex items-center bg-gray-100 border-0 py-1 px-3 focus:outline-none hover:bg-gray-200 rounded text-base z-10">
            Wishlist
          </button>
        </Link>

        {/* my tools button */}
        <Link to="/my-tools">
          <button className="absolute right-5 top-40 login inline-flex items-center bg-gray-100 border-0 py-1 px-2 focus:outline-none hover:bg-gray-200 rounded text-base z-10">
            My Tools
          </button>
        </Link>
      </>

      {/* profile info and buttons */}
      <div className="profile-card bg-white shadow-md rounded-lg w-full max-w-md p-6">
        {error ? (
          <p className="text-red-500">{error}</p>
        ) : user ? (
          <div>
            {/* top details */}
            <div className="profile-header text-center mb-6">
              <h2
                className="text-3xl font-bold mb-2"
                style={{ color: "black" }}
              >
                Welcome, {user.username}!
              </h2>
              <p className="text-gray-600" style={{ color: "black" }}>
                Email: {user.email}
              </p>

              {user.address && (
                <p className="text-gray-600 mt-4" style={{ color: "black" }}>
                  Address: {user.address.streetAddress}, {user.address.city} -{" "}
                  {user.address.postalCode}
                </p>
              )}
            </div>

            {/* subscription */}
            <div className="subscription-status bg-blue-100 p-4 rounded mb-6">
              <h3 className="text-xl font-semibold mb-2">
                Subscription Status
              </h3>
              <p style={{ color: "black" }}>Plan: {subscription.plan}</p>
              <p style={{ color: "black" }}>Status: {subscription.status}</p>
              <p style={{ color: "black" }}>
                Expires On: {subscription.expiresOn}
              </p>
            </div>

            {/* tools purchased */}
            <div className="tools bg-green-100 p-4 rounded mb-6">
              <h3 className="text-xl font-semibold mb-2">Tools Purchased</h3>

              {purchasedTools.length === 0 ? (
                <div className="text-center text-gray-400">
                  You haven't purchased any tools yet.
                </div>
              ) : (
                purchasedTools.slice(0, 3).map((order, index) => (
                  <div
                    key={index}
                    className={`flex justify-between ${
                      index < 2 ? "border-b border-gray-300 pb-2 mb-2" : ""
                    }`}
                  >
                    <h3 className="text-md font-semibold">
                      {order.tool.toolName}
                    </h3>
                    <span className="text-blue-400">Purchased</span>
                  </div>
                ))
              )}
            </div>

            {message && (
              <p className="mt-4 text-red-700 text-center">{message}</p>
            )}

            {/* buttons */}
            <>
              <button
                onClick={() => setIsUsernameDialog(true)}
                className="logout-btn w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 mb-3"
              >
                Edit Personal Details
              </button>

              <button
                onClick={() => setIsPasswordDialog(true)}
                className="logout-btn w-full bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700 mb-3"
              >
                Change Password
              </button>

              <button
                onClick={() => showConfirmationDialog("logout")}
                className="logout-btn w-full bg-red-500 text-white py-2 px-4 rounded hover:bg-red-700"
              >
                Logout
              </button>

              <Link to="/terms-and-conditions">
                <button
                  className="w-full text-blue-500 font-bold text-center hover:text-blue-700 py-2"
                  style={{ display: "block" }}
                >
                  Terms and Conditions
                </button>
              </Link>
            </>
          </div>
        ) : (
          <p>Loading user information...</p>
        )}
      </div>

      {/* Dialog Box for Editing Personal Details */}
      {isUsernameDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            {/* name */}
            <>
              <h3 className="text-2xl font-semibold mb-4">Edit Name</h3>

              <div className="mb-4">
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg"
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter new username"
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setIsUsernameDialog(false)}
                  className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => showConfirmationDialog("username")}
                  className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </>

            {/* address */}
            <>
              <h3 className="text-2xl font-semibold mb-4 mt-10">
                Edit Address
              </h3>

              <div className="mb-4">
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg"
                  value={streetAddress}
                  onChange={(e) => setStreetAddress(e.target.value)}
                  placeholder="Enter street address"
                />
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg"
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Enter city"
                />
              </div>

              <div className="mb-4">
                <input
                  type="text"
                  className="w-full px-4 py-2 border rounded-lg"
                  value={postalCode}
                  onChange={(e) => setPostalCode(e.target.value)}
                  placeholder="Enter postal code"
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setIsUsernameDialog(false)}
                  className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => showConfirmationDialog("address")}
                  className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </>
          </div>
        </div>
      )}

      {/* Dialog Box for Changing Password */}
      {isPasswordDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-2xl font-semibold mb-4">Change Password</h3>

            <div className="mb-4">
              <input
                type="password"
                className="w-full px-4 py-2 border rounded-lg"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Current password"
              />
            </div>
            <div className="mb-4">
              <input
                type="password"
                className="w-full px-4 py-2 border rounded-lg"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="New password"
              />
            </div>
            <div className="mb-4">
              <input
                type="password"
                className="w-full px-4 py-2 border rounded-lg"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </div>

            <div className="flex justify-between">
              <button
                onClick={() => setIsPasswordDialog(false)}
                className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => showConfirmationDialog("password")}
                className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-xl font-semibold mb-4">
              Are you sure you want to proceed?
            </h3>
            <div className="flex justify-between">
              <button
                onClick={() => setShowConfirmation(false)}
                className="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmation}
                className="bg-blue-500 text-white py-2 px-4 rounded hover:bg-blue-700"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default Profile;
